import { Routes } from '@angular/router';
import { LoginComponent } from './auth/login/login.component';
import { PlannerComponent } from './planner/planner.component';
import { UsersComponent } from './users/users.component';
import { SettingsComponent } from './settings/settings.component';
import { ScheduleComponent } from './schedule/schedule.component';
import { ScheduleLandingComponent } from './schedule/components/landing/landing.component';
import { ScheduleEmployeeListComponent } from './schedule/components/employee-list/employee-list.component';
import { DayPartsComponent } from './day-parts/day-parts.component';
import { AdminToolsComponent } from './admin-tools/admin-tools.component';
import { SetupPaymentMethodsComponent } from './admin-tools/dialogs/setup-payment-methods/setup-payment-methods.component';
import { SelectingOrganisationComponent } from './selecting-organisation/selecting-organisation.component';
import { SetOrganisationComponent } from './set-organisation/set-organisation.component';
import { DeletedRidesComponent } from './deleted-rides/deleted-rides.component';
import { DaypartRideComponent } from './schedule/tabs/planning/pages/daypart-ride/daypart-ride.component';
import { redirectLoggedIn, redirectToLogin, redirectUnauthorized } from './permissions.guard';
import { RidePreparationComponent } from './ride-preparation/ride-preparation.component';
import { TransportComponent } from './transport/transport.component';
import { FinancialComponent } from './financial/financial.component';
import { EmployeesComponent } from './employees/employees.component';
import { AvailabilityOverviewComponent } from './schedule/components/availability-overview/availability-overview.component';
import { AccountComponent } from './account/account.component';
import { Role } from './enums';
import { environment } from 'src/environments/environment';
import { ActionsComponent } from './auth/actions/actions.component';

const allRoutes: Record<string, any> = {
  dayparts: {
    path: 'dayparts',
    component: DayPartsComponent,
    canActivate: [redirectUnauthorized],
    data: { authorizedRights: [Role.Administrator] },
  },
  scheduleLanding: {
    path: 'schedule',
    component: ScheduleLandingComponent,
    canActivate: [redirectUnauthorized],
    data: {
      authorizedRights: [Role.Administrator, Role.Planner, Role.RidePlanner],
    },
  },
  scheduleEmployees: {
    path: 'schedule/employees',
    component: ScheduleEmployeeListComponent,
    canActivate: [redirectUnauthorized],
    data: {
      authorizedRights: [Role.Administrator, Role.Planner],
    },
  },
  availabilityOverview: {
    path: 'schedule/availability',
    component: AvailabilityOverviewComponent,
    canActivate: [redirectUnauthorized],
    data: {
      authorizedRights: [Role.Administrator, Role.Planner, Role.RidePlanner],
    },
  },
  schedule: {
    path: 'schedule/:type/:userId',
    component: ScheduleComponent,
    canActivate: [redirectUnauthorized],
    data: {
      authorizedRights: [
        Role.Administrator,
        Role.Planner,
        Role.RidePlanner,
        Role.Driver,
        Role.CoDriver,
      ],
    },
  },
  daypartPlanning: {
    path: 'planning/:type/daypart/:id',
    component: DaypartRideComponent,
    canActivate: [redirectUnauthorized],
    data: {
      authorizedRights: [
        Role.Administrator,
        Role.Planner,
        Role.RidePlanner,
        Role.Driver,
        Role.CoDriver,
      ],
    },
  },
  login: {
    path: 'login',
    component: LoginComponent,
    canActivate: [redirectLoggedIn],
    data: { view: 'login' },
    children: [
      {
        path: 'reset-password',
        component: LoginComponent,
        data: { view: 'reset-password' },
      },
    ],
  },
  plannen: {
    path: 'plannen',
    component: PlannerComponent,
    canActivate: [redirectUnauthorized],
    data: { authorizedRights: [Role.Administrator, Role.Planner, Role.RidePlanner] },
    children: [
      {
        path: 'rides',
        component: PlannerComponent,
        data: { authorizedRights: [Role.Administrator, Role.Planner, Role.RidePlanner] },
      },
      {
        path: 'dayparts-driver',
        component: PlannerComponent,
        data: { authorizedRights: [Role.Administrator, Role.Planner, Role.RidePlanner] },
      },
      {
        path: 'dayparts-planner',
        component: PlannerComponent,
        data: { authorizedRights: [Role.Administrator, Role.Planner, Role.RidePlanner] },
      },
      {
        path: 'automatic',
        component: PlannerComponent,
        data: { authorizedRights: [Role.Administrator, Role.Planner, Role.RidePlanner] },
      },
    ],
  },
  users: {
    path: 'users',
    component: UsersComponent,
    canActivate: [redirectUnauthorized],
    canDeactivate: [(component: UsersComponent) => component.canDeactivate()],
    data: { authorizedRights: [Role.Administrator, Role.Planner, Role.RidePlanner] },
  },
  employees: {
    path: 'employees',
    component: EmployeesComponent,
    canActivate: [redirectUnauthorized],
  },
  adminTools: {
    path: 'admin-tools',
    component: AdminToolsComponent,
    canActivate: [redirectUnauthorized],
    data: {},
  },
  adminToolsSetupPaymentMethods: {
    path: 'admin-tools/setup-payment-methods',
    component: SetupPaymentMethodsComponent,
    canActivate: [redirectUnauthorized],
    data: {},
  },
  deletedRides: {
    path: 'deleted-rides',
    component: DeletedRidesComponent,
    canActivate: [redirectUnauthorized],
    data: { authorizedRights: [Role.Administrator, Role.Planner] },
  },
  settings: {
    path: 'settings',
    component: SettingsComponent,
    canActivate: [redirectUnauthorized],
    data: { authorizedRights: [Role.Administrator] },
  },
  financial: {
    path: 'financial',
    component: FinancialComponent,
    canActivate: [redirectUnauthorized],
    data: { authorizedRights: [Role.Administrator, Role.Planner] },
    children: [
      {
        path: 'audits',
        component: FinancialComponent,
        data: { authorizedRights: [Role.Administrator, Role.Planner] },
      },
      {
        path: 'debit-orders',
        component: FinancialComponent,
        data: { authorizedRights: [Role.Administrator] },
      },
      {
        path: 'payment-plans',
        component: FinancialComponent,
        data: { authorizedRights: [Role.Administrator] },
      },
    ],
  },
  ridePreparation: {
    path: 'ride-preparation',
    component: RidePreparationComponent,
    canActivate: [redirectUnauthorized],
    data: { authorizedRights: [Role.Administrator] },
    children: [
      {
        path: 'characteristics',
        component: RidePreparationComponent,
        data: { authorizedRights: [Role.Administrator] },
      },
      {
        path: 'standard-locations',
        component: RidePreparationComponent,
        data: { authorizedRights: [Role.Administrator] },
      },
      {
        path: 'phone-numbers',
        component: RidePreparationComponent,
        data: { authorizedRights: [Role.Administrator] },
      },
      {
        path: 'particularities',
        component: RidePreparationComponent,
        data: { authorizedRights: [Role.Administrator] },
      },
      {
        path: 'vehicles',
        redirectTo: '/transport/vehicles',
        pathMatch: 'full',
      },
      {
        path: 'vehicle-groups',
        redirectTo: '/transport/vehicle-groups',
        pathMatch: 'full',
      },
      {
        path: 'rates',
        redirectTo: '/transport/rates',
        pathMatch: 'full',
      },
    ],
  },
  transport: {
    path: 'transport',
    component: TransportComponent,
    canActivate: [redirectUnauthorized],
    data: { authorizedRights: [Role.Administrator] },
    children: [
      {
        path: 'vehicles',
        component: TransportComponent,
        data: { authorizedRights: [Role.Administrator] },
      },
      {
        path: 'vehicle-groups',
        component: TransportComponent,
        data: { authorizedRights: [Role.Administrator] },
      },
      {
        path: 'rates',
        component: TransportComponent,
        data: { authorizedRights: [Role.Administrator] },
      },
    ],
  },
  selectingOrganisation: {
    path: 'selectingOrganisation',
    component: SelectingOrganisationComponent,
    canActivate: [redirectToLogin],
    data: {},
  },
  account: {
    path: 'account',
    component: AccountComponent,
    canActivate: [redirectUnauthorized],
    data: {
      authorizedRights: [
        Role.Administrator,
        Role.Planner,
        Role.RidePlanner,
        Role.Driver,
        Role.CoDriver,
        Role.Other,
      ],
    },
  },
  set: {
    path: 'set/:orgId',
    component: SetOrganisationComponent,
  },
  actions: {
    path: 'auth/actions',
    component: ActionsComponent,
  },
};

const configuredRoutes = environment.routes.map((r) => allRoutes[r]).filter((r) => !!r);

if (environment.routes.includes('ridePreparation') && allRoutes.transport) {
  configuredRoutes.push(allRoutes.transport);
}

export const routes: Routes = [
  ...configuredRoutes,
  ...(!configuredRoutes.some((r) => r?.path === 'account') && allRoutes.account
    ? [allRoutes.account]
    : []),
  {
    path: '**',
    redirectTo: 'plannen',
    data: {},
  },
];
